import React, { useRef, useLayoutEffect, useState, useCallback } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import razorpayLogo from '../../assets/images/logo/razorpay.png';
import handCursor from '../../assets/images/hand_cursor.png';

gsap.registerPlugin(ScrollTrigger);

// Hand cursor image (used on desktop landing scroll animation)
const CursorIcon = ({ grabbed }) => (
    <img
        src={handCursor}
        alt=""
        aria-hidden="true"
        style={{
            width: 34,
            height: 34,
            display: 'block',
            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))',
            transition: 'transform 0.25s ease',
            transform: grabbed ? 'scale(0.92)' : 'scale(1)'
        }}
    />
);

export const TravelingHand = ({ onScrollProgress, pluginGardenRef }) => {
    const cursorRef = useRef(null);
    const pluginRef = useRef(null);
    const droppedPluginRef = useRef(null);
    const timelineRef = useRef(null);
    const progressRef = useRef(0);
    // Track whether we've captured the Razorpay card's position
    const capturedPosRef = useRef(null);
    const hasFrozenRef = useRef(false);
    const dragTlRef = useRef(null);

    const [cursorState, setCursorState] = useState({
        isGrabbed: false,
        showPlugin: false,
        pluginDropped: false,
        isVisible: false
    });

    const updateStateFromProgress = useCallback((progress) => {
        progressRef.current = progress;

        const newState = {
            isGrabbed: progress >= 0.10 && progress < 0.80,
            showPlugin: progress >= 0.15 && progress < 0.80,
            pluginDropped: progress >= 0.80 && progress < 0.95,
            isVisible: progress >= 0.02 && progress < 0.95
        };

        setCursorState(newState);
        onScrollProgress?.(progress);
    }, [onScrollProgress]);

    useLayoutEffect(() => {
        const cursor = cursorRef.current;
        const plugin = pluginRef.current;
        const droppedPlugin = droppedPluginRef.current;
        const garden = pluginGardenRef?.current;

        if (!cursor) return;

        const heroWrapper = document.querySelector('.hero-wrapper');
        if (!heroWrapper) return;

        ScrollTrigger.getAll().forEach(st => st.kill());
        if (timelineRef.current) {
            timelineRef.current.kill();
        }
        if (dragTlRef.current) {
            dragTlRef.current.kill();
            dragTlRef.current = null;
        }
        hasFrozenRef.current = false;
        capturedPosRef.current = null;

        // End above the laptop terminal
        const endX = 30;
        const endY = 73;

        // Start cursor offscreen / invisible
        gsap.set(cursor, {
            left: `-100vw`,
            top: `-100vh`,
            rotation: 0,
            scale: 1,
            opacity: 0,
            xPercent: -50,
            yPercent: -50
        });

        // Helper: read the real Razorpay card position from PluginGarden
        const getRazorpayViewportPos = () => {
            if (!pluginGardenRef?.current) return null;
            const rect = pluginGardenRef.current.getRazorpayRect();
            if (!rect) return null;
            // Convert to vw/vh
            const vw = (rect.left + rect.width / 2) / window.innerWidth * 100;
            const vh = (rect.top + rect.height / 2) / window.innerHeight * 100;
            return { vw, vh };
        };

        // Build the drag timeline from the captured start position
        const buildDragTimeline = (startPos) => {
            if (!startPos || dragTlRef.current) return;

            const sx = startPos.vw;
            const sy = startPos.vh;
            const razorpayEl = pluginGardenRef?.current?.getRazorpayEl();

            const dragTl = gsap.timeline({
                scrollTrigger: {
                    trigger: heroWrapper,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: 1,
                }
            });

            // Phase: Grab — slight movement (8-12%)
            dragTl.fromTo(cursor,
                {
                    left: sx + 'vw',
                    top: sy + 'vh',
                    scale: 1,
                    opacity: 1
                },
                {
                    left: (sx - 1) + 'vw',
                    top: (sy + 1) + 'vh',
                    scale: 1.05,
                    opacity: 1,
                    duration: 0.04,
                    ease: 'power2.inOut',
                    immediateRender: false
                },
                0.08
            );

            // Phase: Hide real card, show carried plugin (12%)
            dragTl.to({}, {
                duration: 0.01,
                onStart: () => {
                    if (razorpayEl) razorpayEl.style.opacity = '0';
                },
                onReverseComplete: () => {
                    if (razorpayEl) razorpayEl.style.opacity = '1';
                }
            }, 0.12);

            // Phase: Lift up (15-20%)
            dragTl.to(cursor, {
                left: (sx - 3) + 'vw',
                top: (sy - 4) + 'vh',
                scale: 1.05,
                duration: 0.05,
                ease: 'power2.out',
                immediateRender: false
            }, 0.15);

            // Phase: Drag diagonally to laptop terminal (20-75%)
            dragTl.to(cursor, {
                left: endX + 'vw',
                top: endY + 'vh',
                scale: 1,
                duration: 0.55,
                ease: 'power1.inOut',
                immediateRender: false
            }, 0.20);

            // Phase: Arrive at drop zone (75-80%)
            dragTl.to(cursor, {
                left: (endX - 2) + 'vw',
                top: (endY + 5) + 'vh',
                scale: 1,
                duration: 0.05,
                ease: 'power2.out',
                immediateRender: false
            }, 0.75);

            // Phase: Release and pull back (80-85%)
            dragTl.to(cursor, {
                left: (endX - 5) + 'vw',
                top: (endY - 5) + 'vh',
                scale: 1,
                duration: 0.05,
                ease: 'power2.in',
                immediateRender: false
            }, 0.80);

            // Phase: Fade out (85-100%)
            dragTl.to(cursor, {
                left: (endX - 10) + 'vw',
                top: (endY - 15) + 'vh',
                scale: 0.9,
                opacity: 0,
                duration: 0.15,
                ease: 'power2.in',
                immediateRender: false
            }, 0.85);

            dragTlRef.current = dragTl;
        };

        const tl = gsap.timeline({
            scrollTrigger: {
                trigger: heroWrapper,
                start: 'top top',
                end: 'bottom bottom',
                scrub: 1,
                onUpdate: (self) => {
                    const progress = self.progress;
                    updateStateFromProgress(progress);

                    // === Before grab: track cursor on top of Razorpay card ===
                    if (progress >= 0.02 && progress < 0.08 && !hasFrozenRef.current) {
                        const pos = getRazorpayViewportPos();
                        if (pos) {
                            capturedPosRef.current = pos;
                            gsap.set(cursor, {
                                left: pos.vw + 'vw',
                                top: pos.vh + 'vh',
                                opacity: Math.min(1, (progress - 0.02) / 0.03),
                                scale: 1
                            });
                        }
                    }

                    // === Freeze the card and build drag timeline ===
                    if (progress >= 0.08 && !hasFrozenRef.current) {
                        if (pluginGardenRef?.current) {
                            const pos = getRazorpayViewportPos();
                            if (pos) capturedPosRef.current = pos;
                            pluginGardenRef.current.freeze();
                            hasFrozenRef.current = true;
                            buildDragTimeline(capturedPosRef.current);
                        }
                    }

                    // === Unfreeze when scrolling back up ===
                    if (progress < 0.05 && hasFrozenRef.current) {
                        if (pluginGardenRef?.current) {
                            pluginGardenRef.current.unfreeze();
                            const el = pluginGardenRef.current.getRazorpayEl();
                            if (el) el.style.opacity = '1';
                        }
                        hasFrozenRef.current = false;
                        if (dragTlRef.current) {
                            dragTlRef.current.kill();
                            dragTlRef.current = null;
                        }
                    }
                }
            }
        });

        timelineRef.current = tl;

        // ===== PLUGIN CARD follows cursor during drag =====
        if (plugin) {
            gsap.set(plugin, { opacity: 0, scale: 0.5 });

            tl.to(plugin,
                {
                    opacity: 1,
                    scale: 1,
                    duration: 0.05,
                    ease: 'back.out',
                    immediateRender: false
                },
                0.15
            );

            tl.to(plugin,
                {
                    opacity: 0,
                    scale: 0.8,
                    duration: 0.05,
                    ease: 'power2.in',
                    immediateRender: false
                },
                0.80
            );
        }

        // ====== DROPPED PLUGIN falls into terminal =====
        if (droppedPlugin) {
            gsap.set(droppedPlugin, { opacity: 0, y: 0, scale: 1 });

            tl.to(droppedPlugin, {
                opacity: 1,
                y: 0,
                scale: 1,
                duration: 0.02,
                ease: 'none',
                immediateRender: false
            }, 0.80);

            tl.to(droppedPlugin, {
                y: 60,
                scale: 0.7,
                opacity: 0,
                duration: 0.08,
                ease: 'power2.in',
                immediateRender: false
            }, 0.82);
        }

        return () => {
            ScrollTrigger.getAll().forEach(st => st.kill());
            if (timelineRef.current) {
                timelineRef.current.kill();
            }
            if (dragTlRef.current) {
                dragTlRef.current.kill();
            }
            // Ensure card is visible on cleanup
            if (garden) {
                garden.unfreeze();
                const el = garden.getRazorpayEl();
                if (el) el.style.opacity = '1';
            }
            hasFrozenRef.current = false;
        };
    }, [updateStateFromProgress, pluginGardenRef]);

    const { isGrabbed, showPlugin, isVisible } = cursorState;

    return (
        <>
            <div
                ref={cursorRef}
                className="traveling-hand"
                style={{
                    position: 'fixed',
                    left: '-100vw',
                    top: '-100vh',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1000,
                    pointerEvents: 'none',
                    willChange: 'transform, opacity, left, top',
                    visibility: isVisible ? 'visible' : 'hidden'
                }}
            >
                {/* Plugin card attached to cursor */}
                <div
                    ref={pluginRef}
                    style={{
                        position: 'absolute',
                        left: '-20px',
                        top: '-45px',
                        transform: 'rotate(-3deg) scale(0.85)',
                        opacity: 0,
                        willChange: 'transform, opacity',
                        zIndex: 1
                    }}
                >
                    <div
                        style={{
                            width: '65px',
                            height: '65px',
                            background: '#ffffff',
                            border: 'none',
                            borderRadius: '12px',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.12)',
                            overflow: 'hidden',
                            position: 'relative'
                        }}
                    >
                        <img
                            src={razorpayLogo}
                            alt="Razorpay MCP"
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                    </div>
                </div>

                {/* Cursor icon (rendered above the carried icon) */}
                <div style={{ position: 'relative', zIndex: 2 }}>
                    <CursorIcon grabbed={isGrabbed} />
                </div>
            </div>

            {/* Dropped plugin (falls into terminal) */}
            <div
                ref={droppedPluginRef}
                style={{
                    position: 'fixed',
                    left: '26vw',
                    top: '60vh',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 999,
                    pointerEvents: 'none',
                    opacity: 0,
                    willChange: 'transform, opacity'
                }}
            >
                <div
                    style={{
                        width: '80px',
                        height: '80px',
                        background: '#000000',
                        border: '2px solid #00ff88',
                        borderRadius: '10px',
                        boxShadow: '0 0 50px rgba(0,255,136,0.7)',
                        overflow: 'hidden'
                    }}
                >
                    <img
                        src={razorpayLogo}
                        alt="Razorpay MCP"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                </div>
            </div>
        </>
    );
};

export default TravelingHand;
